import { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 110;
const LINK_DIST = 150;
const BASE_W = 1200;
const BASE_H = 400;

/** Palette used by the connected dot network (links tint toward the warmer end for depth). */
const PARTICLE_COLORS: Array<[number, number, number]> = [
  [255, 255, 255], // white
  [186, 230, 253], // sky-200
  [196, 181, 253], // violet-300
  [244, 114, 182], // pink-400
  [103, 232, 249], // cyan-300
];

/** Darker, saturated nodes for visibility on the light gradient canvas */
const PARTICLE_COLORS_LIGHT: Array<[number, number, number]> = [
  [91, 33, 182], // violet-700
  [29, 78, 216], // blue-600
  [126, 34, 206], // purple-700
  [8, 145, 178], // cyan-600
  [71, 85, 105], // slate-600
];

export type LandingHeroAppearance = 'dark' | 'light';

type LandingHeroBackgroundProps = {
  /** `light` softens the scene for pale page chrome; default matches the marketing homepage. */
  appearance?: LandingHeroAppearance;
  /**
   * `full` = paints its own gradient (landing hero).
   * `login-overlay` = no backdrop fill; orbs/ribbons/mesh/canvas/grain only so a parent wallpaper (e.g. `login-glass-bg`) shows through.
   */
  layerMode?: 'full' | 'login-overlay';
};

/** Random integer helper; inclusive of both bounds. */
function rng(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function buildWavePath(
  amplitude: number,
  frequency: number,
  phase: number,
  yOffset: number,
  width = BASE_W * 2,
): string {
  const step = 24;
  let d = `M 0 ${yOffset + amplitude * Math.sin(phase)}`;
  for (let x = step; x <= width; x += step) {
    const y = yOffset + amplitude * Math.sin(x * frequency + phase);
    d += ` L ${x} ${y}`;
  }
  return d;
}

/** Inline SVG noise filter for the grain overlay (cheaper than shipping an image). */
const GRAIN_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'>
      <filter id='n'>
        <feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>
        <feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/>
      </filter>
      <rect width='100%' height='100%' filter='url(#n)' opacity='0.55'/>
    </svg>`,
  );

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Depth layer 0..1; larger = closer (bigger + brighter). */
  z: number;
  color: [number, number, number];
  /** For hub pulse; phase offset in radians. */
  pulse: number;
  hub: boolean;
};

type Star = { x: number; y: number; r: number; phase: number; period: number };

type Streak = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
};

/** Full-bleed animated layers for the landing hero (gradient, orbs, ribbons, mesh, particles). */
export default function LandingHeroBackground({
  appearance = 'dark',
  layerMode = 'full',
}: LandingHeroBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const starsRef = useRef<Star[]>([]);
  const streakRef = useRef<Streak | null>(null);
  const mouseRef = useRef<{ tx: number; ty: number; x: number; y: number }>({
    tx: 0,
    ty: 0,
    x: 0,
    y: 0,
  });
  const rafRef = useRef<number>(0);
  const lastStreakAtRef = useRef<number>(0);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const parallax = parallaxRef.current;
    if (!container || !canvas) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    const isLight = appearance === 'light';

    const spawnParticles = () => {
      const palette = isLight ? PARTICLE_COLORS_LIGHT : PARTICLE_COLORS;
      particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => {
        const speed = rng(0.08, 0.28);
        const angle = Math.random() * Math.PI * 2;
        const color = palette[Math.floor(Math.random() * palette.length)];
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          z: Math.random(),
          color,
          pulse: Math.random() * Math.PI * 2,
          hub: Math.random() < 0.08,
        };
      });
    };

    const spawnStars = () => {
      const count = Math.round((w * h) / 18000);
      starsRef.current = Array.from({ length: Math.min(180, Math.max(40, count)) }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: rng(0.4, 1.3),
        phase: Math.random() * Math.PI * 2,
        period: rng(2.2, 5.5),
      }));
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, w, h);
      for (const s of starsRef.current) {
        if (isLight) {
          const a = 0.28 * (0.6 + 0.4 * Math.sin(s.phase));
          ctx.fillStyle = `rgba(71,85,105,${a.toFixed(3)})`;
        } else {
          ctx.fillStyle = `rgba(255,255,255,${0.5 * (0.6 + 0.4 * Math.sin(s.phase))})`;
        }
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      const pts = particlesRef.current;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          if (dx * dx + dy * dy < LINK_DIST * LINK_DIST) {
            ctx.strokeStyle = isLight ? 'rgba(91,33,182,0.07)' : 'rgba(196,181,253,0.08)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      for (const p of pts) {
        const [r, g, b] = p.color;
        const base = isLight ? 0.35 + p.z * 0.35 : 0.55 + p.z * 0.35;
        ctx.fillStyle = `rgba(${r},${g},${b},${base})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 0.9 + p.z * 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      spawnParticles();
      spawnStars();
      if (reducedMotion) drawStatic();
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const onPointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        mouseRef.current.tx = 0;
        mouseRef.current.ty = 0;
        return;
      }
      const nx = (e.clientX - rect.left) / rect.width - 0.5;
      const ny = (e.clientY - rect.top) / rect.height - 0.5;
      mouseRef.current.tx = Math.max(-0.5, Math.min(0.5, nx));
      mouseRef.current.ty = Math.max(-0.5, Math.min(0.5, ny));
    };
    const onPointerLeave = () => {
      mouseRef.current.tx = 0;
      mouseRef.current.ty = 0;
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave);

    const maybeSpawnStreak = (now: number) => {
      if (streakRef.current || now - lastStreakAtRef.current < 4200 + Math.random() * 3500) return;
      lastStreakAtRef.current = now;
      const fromTop = Math.random() > 0.5;
      const startX = rng(w * 0.1, w * 0.9);
      const angle = rng(-0.2, 0.2) + (fromTop ? Math.PI / 2 : -Math.PI / 2);
      const speed = rng(7, 10);
      streakRef.current = {
        x: startX,
        y: fromTop ? -20 : h + 20,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        maxLife: 90,
      };
    };

    const tick = (tMs: number) => {
      const pts = particlesRef.current;
      const stars = starsRef.current;
      const t = tMs * 0.001;

      // Smooth-follow mouse parallax
      const m = mouseRef.current;
      m.x += (m.tx - m.x) * 0.06;
      m.y += (m.ty - m.y) * 0.06;
      if (parallax) {
        parallax.style.transform = `translate3d(${m.x * 24}px, ${m.y * 24}px, 0)`;
      }

      ctx.clearRect(0, 0, w, h);

      // Twinkling starfield (drawn under network for depth)
      for (const s of stars) {
        const a = 0.35 + 0.45 * (0.5 + 0.5 * Math.sin(s.phase + (t * Math.PI * 2) / s.period));
        ctx.fillStyle = isLight
          ? `rgba(71,85,105,${(a * 0.55).toFixed(3)})`
          : `rgba(255,255,255,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Update particles (parallax scaled by depth for subtle motion)
      for (const p of pts) {
        p.x += p.vx + m.x * (0.15 + p.z * 0.35);
        p.y += p.vy + m.y * (0.15 + p.z * 0.35);
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        p.x = Math.max(0, Math.min(w, p.x));
        p.y = Math.max(0, Math.min(h, p.y));
        p.pulse += 0.02;
      }

      // Links (colored by participants; depth biases alpha)
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        for (let j = i + 1; j < pts.length; j++) {
          const b = pts[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < LINK_DIST * LINK_DIST) {
            const dist = Math.sqrt(d2);
            const depth = (a.z + b.z) * 0.5;
            const linkScale = isLight ? 0.55 : 1;
            const alpha = (1 - dist / LINK_DIST) * (0.08 + depth * 0.1) * linkScale;
            // Blend both particle colors for richer lines
            const cr = Math.round((a.color[0] + b.color[0]) / 2);
            const cg = Math.round((a.color[1] + b.color[1]) / 2);
            const cb = Math.round((a.color[2] + b.color[2]) / 2);
            ctx.strokeStyle = `rgba(${cr},${cg},${cb},${alpha.toFixed(3)})`;
            ctx.lineWidth = 0.6 + depth * 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Particles (glow via shadowBlur only on hubs to keep cost sane)
      for (const p of pts) {
        const [r, g, b] = p.color;
        const size = 0.8 + p.z * 1.6;
        const baseAlpha = (isLight ? 0.42 : 0.5) + p.z * (isLight ? 0.38 : 0.45);
        if (p.hub) {
          const pulse = 0.6 + 0.4 * Math.sin(p.pulse);
          ctx.shadowBlur = isLight ? 8 : 10;
          ctx.shadowColor = `rgba(${r},${g},${b},${(0.7 * pulse).toFixed(3)})`;
          ctx.fillStyle = `rgba(${r},${g},${b},${Math.min(1, baseAlpha + 0.2)})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 1.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = `rgba(${r},${g},${b},${baseAlpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Occasional shooting streak
      maybeSpawnStreak(tMs);
      const streak = streakRef.current;
      if (streak) {
        streak.life++;
        streak.x += streak.vx;
        streak.y += streak.vy;
        const ratio = streak.life / streak.maxLife;
        const alpha = Math.sin(Math.min(1, ratio) * Math.PI) * 0.75;
        const tailLen = 80;
        const tx = streak.x - streak.vx * (tailLen / Math.hypot(streak.vx, streak.vy));
        const ty = streak.y - streak.vy * (tailLen / Math.hypot(streak.vx, streak.vy));
        const grad = ctx.createLinearGradient(tx, ty, streak.x, streak.y);
        if (isLight) {
          grad.addColorStop(0, 'rgba(124,58,237,0)');
          grad.addColorStop(1, `rgba(91,33,182,${(alpha * 0.85).toFixed(3)})`);
        } else {
          grad.addColorStop(0, 'rgba(196,181,253,0)');
          grad.addColorStop(1, `rgba(255,255,255,${alpha.toFixed(3)})`);
        }
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(streak.x, streak.y);
        ctx.stroke();
        ctx.fillStyle = isLight
          ? `rgba(91,33,182,${(alpha * 0.9).toFixed(3)})`
          : `rgba(255,255,255,${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(streak.x, streak.y, 1.5, 0, Math.PI * 2);
        ctx.fill();
        if (
          streak.life >= streak.maxLife ||
          streak.x < -40 ||
          streak.x > w + 40 ||
          streak.y < -40 ||
          streak.y > h + 40
        ) {
          streakRef.current = null;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    if (!reducedMotion) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      ro.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, [appearance]);

  // Precomputed ribbon paths (SVG)
  const wavePurple = buildWavePath(42, 0.008, 0, BASE_H * 0.35);
  const wavePink = buildWavePath(36, 0.01, 1.2, BASE_H * 0.5);
  const waveBlue = buildWavePath(48, 0.007, 2.1, BASE_H * 0.65);
  const waveViolet = buildWavePath(32, 0.009, 0.5, BASE_H * 0.8);
  const waveCyan = buildWavePath(30, 0.012, 3.0, BASE_H * 0.25);

  const meshes = [
    {
      shape: 'tri' as const,
      className: 'left-[6%] top-[18%] w-[100px] h-[100px]',
      anim: 'motion-safe:animate-[landing-mesh-drift_28s_linear_infinite]',
    },
    {
      shape: 'hex' as const,
      className: 'right-[12%] top-[28%] w-[140px] h-[140px]',
      anim: 'motion-safe:animate-[landing-mesh-drift-reverse_24s_linear_infinite]',
    },
    {
      shape: 'diamond' as const,
      className: 'left-[22%] bottom-[22%] w-[180px] h-[180px]',
      anim: 'motion-safe:animate-[landing-mesh-drift_32s_linear_infinite]',
    },
    {
      shape: 'tri' as const,
      className: 'right-[20%] bottom-[18%] w-[96px] h-[96px]',
      anim: 'motion-safe:animate-[landing-mesh-drift-reverse_22s_linear_infinite]',
    },
    {
      shape: 'hex' as const,
      className: 'left-1/2 top-[10%] w-[120px] h-[120px] -translate-x-1/2',
      anim: 'motion-safe:animate-[landing-mesh-drift_26s_linear_infinite]',
    },
    {
      shape: 'diamond' as const,
      className: 'left-[42%] top-[52%] w-[70px] h-[70px]',
      anim: 'motion-safe:animate-[landing-mesh-drift_18s_linear_infinite]',
    },
  ];

  const isLightScene = appearance === 'light';
  const isOverlay = layerMode === 'login-overlay';
  const baseGradient = isLightScene
    ? 'radial-gradient(ellipse 120% 80% at 50% 18%, #ede9fe 0%, #e0e7ff 35%, #f8fafc 70%, #f1f5f9 100%)'
    : 'radial-gradient(ellipse 120% 80% at 50% 18%, #1a0a3a 0%, #12082a 38%, #0a0a1a 72%, #07061a 100%)';
  const vignetteGradient = isLightScene
    ? 'radial-gradient(ellipse 100% 100% at 50% 120%, transparent 52%, rgba(15,23,42,0.08) 100%)'
    : 'radial-gradient(ellipse 100% 100% at 50% 120%, rgba(10,10,26,0) 55%, rgba(0,0,0,0.85) 100%)';
  const orbBlend = isLightScene ? 'mix-blend-multiply' : 'mix-blend-screen';
  const ribbonLayerOpacity = isOverlay
    ? isLightScene
      ? 'opacity-[0.12] motion-reduce:opacity-10'
      : 'opacity-[0.2] motion-reduce:opacity-14'
    : isLightScene
      ? 'opacity-[0.2] motion-reduce:opacity-14'
      : 'opacity-[0.34] motion-reduce:opacity-20';
  const meshLayerOpacity = isOverlay
    ? isLightScene
      ? 'opacity-[0.07]'
      : 'opacity-[0.11]'
    : isLightScene
      ? 'opacity-[0.09]'
      : 'opacity-[0.14]';
  const conicOpacity = isOverlay
    ? isLightScene
      ? 'opacity-[0.035] mix-blend-multiply'
      : 'opacity-[0.05] mix-blend-screen'
    : isLightScene
      ? 'opacity-[0.06] mix-blend-multiply'
      : 'opacity-[0.08] mix-blend-screen';
  const topSheen = isLightScene
    ? 'linear-gradient(180deg, rgba(255,255,255,0.65), transparent)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.06), transparent)';
  const grainOpacity = isOverlay
    ? isLightScene
      ? 'opacity-[0.012] mix-blend-multiply'
      : 'opacity-[0.02] mix-blend-overlay'
    : isLightScene
      ? 'opacity-[0.022] mix-blend-multiply'
      : 'opacity-[0.035] mix-blend-overlay';
  const parallaxDim = isOverlay ? (isLightScene ? 'opacity-[0.42]' : 'opacity-[0.5]') : '';

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* 1. Base radial gradient + vignette (skipped on login overlay — parent wallpaper shows through) */}
      {!isOverlay ? (
        <>
          <div className="absolute inset-0" style={{ background: baseGradient }} />
          <div className="absolute inset-0" style={{ background: vignetteGradient }} />
        </>
      ) : null}

      {/* Parallax layer: orbs + ribbons drift slightly with mouse */}
      <div
        ref={parallaxRef}
        className={`pointer-events-none absolute inset-0 will-change-transform ${parallaxDim}`}
        style={{ transition: 'transform 0.1s linear' }}
      >
        {/* 4. Ambient glow orbs */}
        <div
          className={`motion-safe:animate-landing-orb-breathe absolute -left-32 -top-24 h-[520px] w-[520px] rounded-full will-change-transform ${orbBlend}`}
          style={{
            background: isLightScene
              ? 'radial-gradient(circle, rgba(124,58,237,0.28) 0%, rgba(167,139,250,0.14) 42%, transparent 72%)'
              : 'radial-gradient(circle, rgba(124,58,237,0.55) 0%, rgba(109,40,217,0.25) 40%, transparent 70%)',
          }}
        />
        <div
          className={`motion-safe:animate-landing-orb-breathe-alt absolute -bottom-40 -right-32 h-[600px] w-[600px] rounded-full will-change-transform [animation-delay:-4s] ${orbBlend}`}
          style={{
            background: isLightScene
              ? 'radial-gradient(circle, rgba(13,148,136,0.22) 0%, rgba(6,182,212,0.12) 48%, transparent 74%)'
              : 'radial-gradient(circle, rgba(13,148,136,0.45) 0%, rgba(6,182,212,0.2) 45%, transparent 72%)',
          }}
        />
        <div
          className={`motion-safe:animate-landing-orb-breathe absolute left-1/3 bottom-0 h-[480px] w-[480px] -translate-x-1/2 rounded-full will-change-transform [animation-delay:-2s] ${isLightScene ? 'opacity-80' : 'opacity-70'} ${orbBlend}`}
          style={{
            background: isLightScene
              ? 'radial-gradient(circle, rgba(192,38,211,0.18) 0%, transparent 68%)'
              : 'radial-gradient(circle, rgba(192,38,211,0.32) 0%, transparent 65%)',
          }}
        />
        <div
          className={`motion-safe:animate-landing-orb-breathe-alt absolute -top-24 right-[30%] h-[360px] w-[360px] rounded-full will-change-transform [animation-delay:-6s] ${isLightScene ? 'opacity-90' : 'opacity-80'} ${orbBlend}`}
          style={{
            background: isLightScene
              ? 'radial-gradient(circle, rgba(59,130,246,0.22) 0%, transparent 72%)'
              : 'radial-gradient(circle, rgba(59,130,246,0.35) 0%, transparent 70%)',
          }}
        />

        {/* 3. Glowing wave ribbons (SVG + blur + horizontal drift) */}
        <div className={`absolute inset-0 overflow-hidden ${ribbonLayerOpacity}`}>
          <div className="motion-safe:animate-landing-ribbon-drift-slow absolute -left-1/2 top-[6%] h-[45%] w-[200%] blur-[40px] will-change-transform">
            <svg className="h-full w-full" viewBox={`0 0 ${BASE_W * 2} ${BASE_H}`} preserveAspectRatio="none" aria-hidden>
              <path d={wavePurple} fill="none" stroke="#7c3aed" strokeWidth="36" strokeLinecap="round" />
              <path d={wavePink} fill="none" stroke="#c026d3" strokeWidth="28" strokeLinecap="round" />
            </svg>
          </div>
          <div className="motion-safe:animate-landing-ribbon-drift absolute -left-1/2 top-[40%] h-[42%] w-[200%] blur-[40px] will-change-transform [animation-delay:-15s]">
            <svg className="h-full w-full" viewBox={`0 0 ${BASE_W * 2} ${BASE_H}`} preserveAspectRatio="none" aria-hidden>
              <path d={waveBlue} fill="none" stroke="#3b82f6" strokeWidth="32" strokeLinecap="round" />
              <path d={waveViolet} fill="none" stroke="#7c3aed" strokeWidth="24" strokeLinecap="round" />
            </svg>
          </div>
          {/* Diagonal accent ribbon */}
          <div
            className="motion-safe:animate-landing-ribbon-drift-diag absolute -left-1/2 top-[22%] h-[35%] w-[200%] -rotate-6 blur-[50px] will-change-transform"
            style={{ transformOrigin: '50% 50%' }}
          >
            <svg className="h-full w-full" viewBox={`0 0 ${BASE_W * 2} ${BASE_H}`} preserveAspectRatio="none" aria-hidden>
              <path d={waveCyan} fill="none" stroke="#22d3ee" strokeWidth="18" strokeLinecap="round" />
            </svg>
          </div>
        </div>

        {/* 5a. Rotating conic sheen — very slow, very soft */}
        <div
          className={`motion-safe:animate-landing-conic-sweep absolute left-1/2 top-1/2 h-[140vmax] w-[140vmax] -translate-x-1/2 -translate-y-1/2 will-change-transform ${conicOpacity}`}
          style={{
            background: isLightScene
              ? 'conic-gradient(from 0deg, transparent 0deg, rgba(124,58,237,0.35) 60deg, transparent 120deg, transparent 240deg, rgba(14,165,233,0.28) 300deg, transparent 360deg)'
              : 'conic-gradient(from 0deg, transparent 0deg, rgba(196,181,253,0.5) 60deg, transparent 120deg, transparent 240deg, rgba(103,232,249,0.35) 300deg, transparent 360deg)',
            filter: 'blur(40px)',
          }}
        />
      </div>

      {/* 5b. Wireframe mesh shapes (polygon / hex / diamond) */}
      <div className="absolute inset-0">
        {meshes.map((m, i) => (
          <div key={i} className={`absolute will-change-transform ${meshLayerOpacity} ${m.anim} ${m.className}`}>
            <svg className="h-full w-full" viewBox="0 0 100 100" aria-hidden>
              {m.shape === 'tri' && (
                <>
                  <polygon
                    points="50,8 88,72 12,72"
                    fill="none"
                    stroke={isLightScene ? 'rgba(91,33,182,0.35)' : 'rgba(255,255,255,0.5)'}
                    strokeWidth="0.8"
                  />
                  <polygon
                    points="50,92 12,28 88,28"
                    fill="none"
                    stroke={isLightScene ? 'rgba(37,99,235,0.3)' : 'rgba(147,197,253,0.35)'}
                    strokeWidth="0.6"
                  />
                </>
              )}
              {m.shape === 'hex' && (
                <>
                  <polygon
                    points="50,6 90,28 90,72 50,94 10,72 10,28"
                    fill="none"
                    stroke={isLightScene ? 'rgba(124,58,237,0.38)' : 'rgba(196,181,253,0.55)'}
                    strokeWidth="0.7"
                  />
                  <polygon
                    points="50,22 74,36 74,64 50,78 26,64 26,36"
                    fill="none"
                    stroke={isLightScene ? 'rgba(219,39,119,0.28)' : 'rgba(244,114,182,0.35)'}
                    strokeWidth="0.5"
                  />
                </>
              )}
              {m.shape === 'diamond' && (
                <>
                  <polygon
                    points="50,6 94,50 50,94 6,50"
                    fill="none"
                    stroke={isLightScene ? 'rgba(8,145,178,0.38)' : 'rgba(103,232,249,0.45)'}
                    strokeWidth="0.7"
                  />
                  <polygon
                    points="50,22 78,50 50,78 22,50"
                    fill="none"
                    stroke={isLightScene ? 'rgba(71,85,105,0.28)' : 'rgba(255,255,255,0.3)'}
                    strokeWidth="0.5"
                  />
                </>
              )}
            </svg>
          </div>
        ))}
      </div>

      {/* 2. Particle network canvas (above ribbons/orbs, below text) */}
      <canvas
        ref={canvasRef}
        className={`absolute inset-0 h-full w-full ${isOverlay ? (isLightScene ? 'opacity-90' : 'opacity-95') : ''}`}
        aria-hidden
      />

      {/* Top sheen — only for full scene */}
      {!isOverlay ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-48" style={{ background: topSheen }} />
      ) : null}

      {/* Grain overlay for texture — motion-safe prefers the animated drift */}
      <div
        className={`motion-safe:animate-landing-grain-drift pointer-events-none absolute inset-0 ${grainOpacity}`}
        style={{
          backgroundImage: `url("${GRAIN_SVG}")`,
          backgroundSize: '240px 240px',
          backgroundRepeat: 'repeat',
          willChange: 'transform',
        }}
        aria-hidden
      />
    </div>
  );
}
